import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useClienteImpersonation } from "@/contexts/ClienteImpersonationContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SelectorVerComo, type GrupoVerComo } from "@/components/ui/selector-ver-como";
import { Building2, Home, Eye } from "lucide-react";

type Owner = { email: string; personaId: number | null; nombre: string };

/**
 * Selector "Ver como" del Portal Clientes, pintado con el estándar `SelectorVerComo`.
 * Proyecto + No. de propiedad van en el `encabezado` y resuelven al dueño de la unidad
 * (con copropietarios); los clientes van como grupos de la lista.
 */

export function ClienteImpersonationSelector() {
  const { profile } = useAuth();
  const { impersonatedClienteEmail, impersonatedClienteName, setImpersonatedCliente, clearImpersonation, isImpersonating } = useClienteImpersonation();
  const [proyectoId, setProyectoId] = useState<number | null>(null);
  const [numeroPropiedad, setNumeroPropiedad] = useState("");

  // Homologado con el resto de portales: el selector "Ver como" se muestra
  // solo a roles con el permiso Impersonar usuarios (roles.puede_impersonar).
  const canAccessClientPortal = profile?.puede_impersonar === true;

  const { data: clients = [], isLoading: cargandoClientes } = useQuery({
    queryKey: ["all-clients-for-impersonation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("usuarios")
        .select("email, rol_id, personas!inner(id, nombre_legal)")
        .eq("rol_id", 23)
        .eq("activo", true)
        .order("email");

      if (error) throw error;
      return (data || []).map((u: any) => ({
        email: u.email,
        personaId: u.personas?.id,
        nombre: u.personas?.nombre_legal || u.email,
      })).sort((a: any, b: any) => a.nombre.localeCompare(b.nombre));
    },
    enabled: canAccessClientPortal,
  });

  // Proyectos administrados por SOZU (id_tipo_entidad = 5). Waterfall explícito.
  const { data: proyectos = [] } = useQuery({
    queryKey: ["sozu-proyectos-impersonation"],
    queryFn: async () => {
      const { data: rels, error: relsErr } = await supabase
        .from("entidades_relacionadas")
        .select("id_proyecto")
        .eq("id_tipo_entidad", 5)
        .eq("activo", true);
      if (relsErr) throw relsErr;
      const ids = [...new Set((rels || []).map((r: any) => r.id_proyecto).filter(Boolean))];
      if (!ids.length) return [];
      const { data: proys, error: proysErr } = await supabase
        .from("proyectos")
        .select("id, nombre")
        .in("id", ids)
        .eq("publicar", true)
        .eq("activo", true)
        .order("nombre");
      if (proysErr) throw proysErr;
      return (proys || []) as { id: number; nombre: string }[];
    },
    enabled: canAccessClientPortal,
  });

  // Resuelve TODOS los dueños (lead + copropietarios) a partir de proyecto + número
  // de propiedad. Mismo vínculo que use-portfolio.ts, con soporte de copropiedad:
  //  - cuentas de la propiedad por DOS vías: cuentas.id_propiedad = propId, o su
  //    oferta principal apunta a propId (id_propiedad de la cuenta puede ser NULL).
  //  - personas dueñas = lead de la oferta principal ∪ compradores (copropietarios).
  // Waterfall: proyecto → edificios → edificios_modelos → propiedades →
  // cuentas_cobranza → ofertas/compradores → usuarios (cliente rol 23).
  const numTrim = numeroPropiedad.trim();
  const { data: resolvedOwners = [] } = useQuery({
    queryKey: ["resolve-clientes-by-propiedad", proyectoId, numTrim],
    queryFn: async (): Promise<Owner[]> => {
      const { data: eds } = await supabase
        .from("edificios").select("id").eq("id_proyecto", proyectoId).eq("activo", true);
      const edIds = (eds || []).map((e: any) => e.id);
      if (!edIds.length) return [];

      const { data: mods } = await supabase
        .from("edificios_modelos").select("id").in("id_edificio", edIds);
      const modIds = (mods || []).map((m: any) => m.id);
      if (!modIds.length) return [];

      const { data: props } = await supabase
        .from("propiedades")
        .select("id")
        .in("id_edificio_modelo", modIds)
        .eq("numero_propiedad", numTrim)
        .eq("activo", true)
        .limit(1);
      const propId = props?.[0]?.id;
      if (!propId) return [];

      // Ofertas principales que apuntan a la propiedad (para path por oferta y leads)
      const { data: ofByProp } = await supabase
        .from("ofertas")
        .select("id")
        .eq("id_propiedad", propId)
        .eq("activo", true)
        .is("id_producto", null);
      const ofByPropIds = [...new Set((ofByProp || []).map((o: any) => o.id).filter(Boolean))];

      // Cuentas de la propiedad: (a) por cuentas.id_propiedad, (b) por oferta principal
      const [cByPropRes, cByOfertaRes] = await Promise.all([
        supabase.from("cuentas_cobranza").select("id").eq("id_propiedad", propId).eq("activo", true).eq("es_aprobado", true),
        ofByPropIds.length
          ? supabase.from("cuentas_cobranza").select("id").in("id_oferta", ofByPropIds).eq("activo", true).eq("es_aprobado", true)
          : Promise.resolve({ data: [] as { id: number }[] }),
      ]);
      const cuentaIds = [
        ...new Set([...(cByPropRes.data || []), ...(cByOfertaRes.data || [])].map((c: any) => c.id)),
      ];
      if (!cuentaIds.length) return [];

      // Personas dueñas: leads (ofertas principales de esas cuentas) ∪ copropietarios
      const personaIds = new Set<number>();

      const { data: cuentasFull } = await supabase
        .from("cuentas_cobranza").select("id, id_oferta").in("id", cuentaIds);
      const ofertaIds = [...new Set((cuentasFull || []).map((c: any) => c.id_oferta).filter(Boolean))];
      if (ofertaIds.length) {
        const { data: ofs } = await supabase
          .from("ofertas").select("id_persona_lead, id_producto").in("id", ofertaIds).eq("activo", true);
        for (const o of (ofs || []) as any[]) {
          if (o.id_producto == null && o.id_persona_lead) personaIds.add(o.id_persona_lead as number);
        }
      }

      const { data: comps } = await supabase
        .from("compradores").select("id_persona").in("id_cuenta_cobranza", cuentaIds).eq("activo", true);
      for (const c of (comps || []) as any[]) if (c.id_persona) personaIds.add(c.id_persona as number);

      if (!personaIds.size) return [];

      const { data: us } = await supabase
        .from("usuarios")
        .select("email, personas!inner(id, nombre_legal)")
        .in("id_persona", [...personaIds])
        .eq("rol_id", 23)
        .eq("activo", true);
      return ((us || []) as any[])
        .map((u) => ({ email: u.email, personaId: u.personas?.id ?? null, nombre: u.personas?.nombre_legal || u.email }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    enabled: canAccessClientPortal && proyectoId !== null && numTrim.length > 0,
  });

  // Autoselecciona un dueño cuando proyecto + propiedad resuelven. Si el cliente ya
  // impersonado es uno de los dueños (p.ej. el admin eligió el otro copropietario),
  // respetar esa elección; si no, autoseleccionar el primero.
  const ownersKey = resolvedOwners.map((o) => o.email).join(",");
  useEffect(() => {
    if (!resolvedOwners.length) return;
    if (impersonatedClienteEmail && resolvedOwners.some((o) => o.email === impersonatedClienteEmail)) return;
    const first = resolvedOwners[0];
    setImpersonatedCliente(first.email, first.personaId ?? null, first.nombre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownersKey]);

  // Early return DESPUÉS de todos los hooks (Rules of Hooks): canAccessClientPortal
  // arranca en false mientras profile carga y luego puede pasar a true, así que
  // retornar antes de los useQuery cambiaba el número de hooks entre renders.
  if (!canAccessClientPortal) return null;

  const grupos: GrupoVerComo[] = [];
  if (resolvedOwners.length > 0) {
    grupos.push({
      titulo: resolvedOwners.length > 1 ? "Copropietarios" : "Dueño de la propiedad",
      opciones: resolvedOwners.map((o) => ({
        // Prefijo propio: el mismo cliente aparece también en "Todos los clientes".
        key: `owner-${o.email}`,
        label: o.nombre,
        buscarPor: o.email,
        seleccionada: impersonatedClienteEmail === o.email,
        onSelect: () => setImpersonatedCliente(o.email, o.personaId, o.nombre),
      })),
    });
  }
  grupos.push({
    titulo: "Todos los clientes",
    opciones: clients.map((client: any) => ({
      key: client.email,
      label: client.nombre,
      buscarPor: client.email,
      seleccionada: impersonatedClienteEmail === client.email,
      onSelect: () => setImpersonatedCliente(client.email, client.personaId, client.nombre),
    })),
  });

  // Proyecto + No. de propiedad arriba de la lista: resuelven al dueño (y copropietarios).
  const encabezado = (
    <div className="flex items-center gap-2">
      <Select
        value={proyectoId !== null ? String(proyectoId) : undefined}
        onValueChange={(v) => setProyectoId(Number(v))}
      >
        <SelectTrigger className="h-8 min-w-0 flex-1 text-[12px]" aria-label="Proyecto">
          <Building2 className="mr-1.5 size-3.5 shrink-0 text-muted-foreground" />
          <SelectValue placeholder="Proyecto" />
        </SelectTrigger>
        <SelectContent>
          {proyectos.length === 0 ? (
            <div className="px-2 py-1.5 text-[12px] text-muted-foreground">Sin proyectos disponibles</div>
          ) : (
            proyectos.map((proy) => (
              <SelectItem key={proy.id} value={String(proy.id)} className="text-[12px]">
                {proy.nombre}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <div className="flex h-8 w-[104px] shrink-0 items-center gap-1.5 rounded-md border border-control-border bg-background px-2">
        <Home className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          value={numeroPropiedad}
          onChange={(e) => setNumeroPropiedad(e.target.value)}
          placeholder="No. prop"
          aria-label="Número de propiedad"
          className="w-full min-w-0 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );

  return (
    <SelectorVerComo
      etiqueta={isImpersonating ? impersonatedClienteName || impersonatedClienteEmail || "Cliente" : "Mi vista"}
      impersonando={isImpersonating}
      opcionPorDefecto={{
        key: "__mi_vista__",
        label: "Mi vista",
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <Eye className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={grupos}
      onLimpiar={clearImpersonation}
      ariaLimpiar="Limpiar cliente"
      encabezado={encabezado}
      textoVacio="No se encontró el cliente."
      cargando={cargandoClientes}
    />
  );
}
